// @catalog F.010
/**
 * popover-shell — primitivas compartidas por InventoryPopover + SalesPopover.
 *
 * Spec literal:
 *   - README §5 Popovers (líneas 169-178) — position absolute + min-width 280 +
 *     max-width 320 + padding 12 + box-shadow + border + click outside/ESC cierra.
 *   - README §Comportamiento §Popovers (líneas 224-227) — solo uno abierto + posicionado
 *     con getBoundingClientRect + ajustes para no salir del viewport.
 *   - prototype/forecast-page.jsx 627-694 (MiniPopover render + position calc).
 *   - prototype/styles.css 530-563 (.fc-popover + .fc-popover-title/row/foot).
 */
import React, { useEffect, useMemo, useRef } from "react";
import { Box, Stack } from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PopoverPosition {
  left: number;
  top: number;
}

const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT_ESTIMATE = 280;

/**
 * Calcula posición del popover anclado a un elemento, clamping al viewport.
 * Por default prefiere bottom-right del anchor; si se desborda, ajusta a la
 * izquierda / arriba.
 */
export function computePopoverPosition(
  anchor: AnchorRect,
  viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280,
  viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800,
): PopoverPosition {
  // Por default ancla a la derecha del cell (right edge) y debajo (bottom + 4)
  const desiredLeft = anchor.right - POPOVER_WIDTH;
  const desiredTop = anchor.bottom + 4;
  const left = Math.max(8, Math.min(desiredLeft, viewportWidth - POPOVER_WIDTH - 8));
  const top = Math.max(8, Math.min(desiredTop, viewportHeight - POPOVER_HEIGHT_ESTIMATE - 8));
  return { left, top };
}

export interface PopoverShellProps {
  open: boolean;
  anchorRect: AnchorRect | null;
  onClose: () => void;
  title: string;
  testId?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onOpenFullDialog?: () => void;
}

export function PopoverShell(props: PopoverShellProps): React.ReactElement | null {
  const {
    open,
    anchorRect,
    onClose,
    title,
    testId,
    children,
    footer,
    onOpenFullDialog,
  } = props;
  const ref = useRef<HTMLDivElement | null>(null);

  const position = useMemo(() => {
    if (!anchorRect) return { left: 0, top: 0 };
    return computePopoverPosition(anchorRect);
  }, [anchorRect]);

  useEffect(() => {
    if (!open) return;
    const handleDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // delay para evitar que el click inicial que abrió el popover lo cierre inmediatamente
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", handleDocClick);
    }, 50);
    document.addEventListener("keydown", handleKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open || !anchorRect) return null;

  return (
    <Box
      ref={ref}
      data-testid={testId}
      sx={{
        position: "fixed",
        left: position.left,
        top: position.top,
        bgcolor: "var(--surface)",
        border: "1px solid var(--border-strong)",
        borderRadius: "8px",
        boxShadow: "0 10px 30px rgba(13, 18, 32, 0.12)",
        zIndex: 100,
        padding: "12px",
        minWidth: 280,
        maxWidth: 320,
        fontSize: 12.5,
      }}
    >
      <Box
        component="header"
        sx={{
          fontSize: 11,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          mb: "8px",
        }}
      >
        {title}
      </Box>
      <Box>{children}</Box>
      {footer && (
        <Box
          sx={{
            mt: "8px",
            pt: "8px",
            borderTop: "1px solid var(--border)",
            fontSize: 12,
            display: "flex",
            justifyContent: "space-between",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {footer}
        </Box>
      )}
      {onOpenFullDialog && (
        <Box
          component="button"
          type="button"
          onClick={onOpenFullDialog}
          data-testid={testId ? `${testId}-full-link` : undefined}
          sx={{
            mt: "10px",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            border: 0,
            bgcolor: "transparent",
            color: "var(--accent)",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            fontFamily: "inherit",
            padding: 0,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Ver detalle completo
          <ArrowForwardIcon sx={{ fontSize: 12 }} />
        </Box>
      )}
    </Box>
  );
}

/**
 * Helper para renderizar una fila .fc-popover-row con label + valor(es).
 */
export function PopoverRow({
  label,
  value,
  testId,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  testId?: string;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      data-testid={testId}
      sx={{
        padding: "4px 0",
        borderBottom: "1px solid var(--border)",
        fontVariantNumeric: "tabular-nums",
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Box component="span" sx={{ color: "var(--ink-2)" }}>
        {label}
      </Box>
      <Box component="span" sx={{ fontWeight: 500, color: "var(--ink-1)" }}>
        {value}
      </Box>
    </Stack>
  );
}
